import { useUsageStats } from '../../hooks/useUsageStats';
import { BarChart3, FolderOpen, FileText, BookOpen, Coins, ShieldAlert, CheckCircle2 } from 'lucide-react';

const statusLabels = {
  uploaded: 'Uploaded',
  analyzing: 'Analyzing',
  processing: 'Processing',
  processed: 'Processed',
  completed: 'Completed',
  failed: 'Failed',
  error: 'Error',
};

const statusColors = {
  uploaded: 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200',
  analyzing: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300',
  processing: 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300',
  processed: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300',
  completed: 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300',
  failed: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
  error: 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300',
};

function StatCard({ icon: Icon, label, value, color = 'text-gray-900 dark:text-white' }) {
  return (
    <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-lg p-5">
      <div className="flex items-center gap-3 mb-2">
        <Icon className="w-5 h-5 text-gray-400 dark:text-gray-500" />
        <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
      </div>
      <p className={`text-2xl font-bold ${color}`}>
        {value != null ? value.toLocaleString() : '-'}
      </p>
    </div>
  );
}

export default function UsageSection() {
  const { stats, loading } = useUsageStats();

  if (loading) {
    return (
      <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-lg p-12 text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto" />
        <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">Loading statistics...</p>
      </div>
    );
  }

  if (!stats) {
    return (
      <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-lg p-12 text-center">
        <BarChart3 className="w-10 h-10 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
        <p className="text-gray-500 dark:text-gray-400">No usage data available.</p>
      </div>
    );
  }

  const statusEntries = Object.entries(stats.statusBreakdown || {});

  return (
    <div className="space-y-6">
      {/* Stat Cards Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <StatCard icon={FolderOpen} label="Total Projects" value={stats.totalProjects} />
        <StatCard icon={FileText} label="Total Documents" value={stats.totalDocuments} />
        <StatCard icon={BookOpen} label="Pages Analyzed" value={stats.totalPages} />
        <StatCard icon={Coins} label="Credits Used" value={stats.totalCreditsUsed} />
        <StatCard icon={ShieldAlert} label="PII Items Found" value={stats.totalPiiItems} />
        <StatCard icon={CheckCircle2} label="Processed Documents" value={stats.statusBreakdown?.processed || 0} />
      </div>

      {/* Status Breakdown */}
      {statusEntries.length > 0 && (
        <div className="bg-white dark:bg-dark-bg border border-gray-200 dark:border-dark-border rounded-lg p-6">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-4">
            Document Status Breakdown
          </h3>
          <div className="flex flex-wrap gap-3">
            {statusEntries.map(([status, count]) => (
              <div
                key={status}
                className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium ${statusColors[status] || statusColors.uploaded}`}
              >
                <span>{statusLabels[status] || status}</span>
                <span className="font-bold">{count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
